import { supabase } from './supabase/client';
import type {
  ParsedTransactionResult,
  ParseTransactionError,
  ParseTransactionErrorCode,
} from '@flows.cash/types';

// Generous: the first parse after a user's categories/accounts change pays a
// one-time schema-compilation cost server-side and can take well over 10s.
const CLIENT_TIMEOUT_MS = 30_000;

export type ParseTransactionResponse =
  | { data: ParsedTransactionResult; error: null }
  | { data: null; error: ParseTransactionError };

function errorResponse(code: ParseTransactionErrorCode): ParseTransactionResponse {
  return { data: null, error: { error: code } };
}

/**
 * Sends a natural-language transaction description to the parse-transaction
 * Edge Function and returns structured fields for pre-filling the add form.
 * Never throws — all failures resolve to a ParseTransactionError so callers
 * can fall back to manual entry.
 */
export async function parseTransactionText(
  rawText: string
): Promise<ParseTransactionResponse> {
  const trimmed = rawText.trim();
  if (!trimmed) return errorResponse('parse_unavailable');
  if (trimmed.length > 500) return errorResponse('input_too_long');

  // The user's local date, so "today"/"yesterday" resolve in their timezone
  // rather than the server's.
  const now = new Date();
  const localDate = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  const timeout = new Promise<ParseTransactionResponse>((resolve) =>
    setTimeout(() => resolve(errorResponse('parse_unavailable')), CLIENT_TIMEOUT_MS)
  );

  const request = (async (): Promise<ParseTransactionResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('parse-transaction', {
        body: { rawText: trimmed, localDate },
      });

      if (error) {
        // FunctionsHttpError carries the original Response as `context`.
        const status: number | undefined = (error as any)?.context?.status;
        if (status === 429) return errorResponse('rate_limited');
        if (status === 400) return errorResponse('input_too_long');
        return errorResponse('parse_unavailable');
      }

      if (typeof data?.amount !== 'number' || typeof data?.type !== 'string') {
        return errorResponse('parse_unavailable');
      }

      return { data: data as ParsedTransactionResult, error: null };
    } catch (err) {
      console.error('parseTransactionText failed:', err);
      return errorResponse('parse_unavailable');
    }
  })();

  return Promise.race([request, timeout]);
}
